<script setup>
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch
} from 'vue'
import { useRoute } from 'vue-router'
import {
  DEFAULT_TEMPLATE_ID,
  previewDraft,
  templates
} from '@/stores/websiteBuilderStore'
import { API_BASE_URL } from '@/stores/appConfig'




const route = useRoute()

const loading = ref(true)
const loadError = ref('')
const hasLoaded = ref(false)
const website = reactive(createEmptyWebsite())
const inlineTemplate = ref(null)

// Increments for each load attempt so stale async responses cannot overwrite newer route data.
let loadRun = 0

const routeIdentifier = computed(() =>
  String(route.params.id || '').trim()
)

function hydrateLivePreview(data = {}) {
  const previewWebsite = data.website || {}

  loadRun += 1
  Object.assign(website, createEmptyWebsite(), {
    ...previewWebsite,
    site_title:
      data.site_title ||
      previewWebsite.site_title ||
      '',
    template_id:
      data.template_id ||
      previewWebsite.template_id ||
      DEFAULT_TEMPLATE_ID,
    content: parseContent(data.content ?? previewWebsite.content)
  })

  loading.value = false
  loadError.value = ''
  hasLoaded.value = true
}

function hydrateStorePreview() {
  if (!previewDraft.hasLiveDraft) {
    return
  }

  hydrateLivePreview({
    website: {
      id: previewDraft.websiteId,
      company_id: previewDraft.companyId,
      domain: previewDraft.domain,
      quotastic_domain: previewDraft.quotasticDomain,
      site_title: previewDraft.siteTitle,
      template_id: previewDraft.templateId,
    },
    site_title: previewDraft.siteTitle,
    template_id: previewDraft.templateId,
    content: previewDraft.content
  })
}

function handlePreviewMessage(event) {
  if (
    event.origin !== window.location.origin ||
    event.data?.type !== 'quotastic:builder-preview'
  ) {
    return
  }

  if (Object.hasOwn(event.data, 'inline_template')) {
    inlineTemplate.value = event.data.inline_template || null
  }

  hydrateLivePreview(event.data)
}

onMounted(() => {
  window.addEventListener('message', handlePreviewMessage)

  if (window.parent === window) {
    hydrateStorePreview()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('message', handlePreviewMessage)
})

// Resolve the active template, falling back through known defaults if saved data is missing.
const selectedTemplate = computed(() => {
  if (inlineTemplate.value) return inlineTemplate.value

  const templateId =
    website.template_id ||
    previewDraft.templateId ||
    DEFAULT_TEMPLATE_ID

  return (
    templates.find(t => t.id === templateId) ||
    templates.find(t => t.id === DEFAULT_TEMPLATE_ID) ||
    templates[0] ||
    null
  )
})

// Start with template defaults, then overlay saved or locally edited website content.
const renderedContent = computed(() => {
  const templateContent = cloneContent(selectedTemplate.value?.content || {})
  const savedContent = parseContent(website.content)

  return mergeContent(templateContent, savedContent)
})

// Use the best available site-specific label for the browser tab.
const pageTitle = computed(() =>
  text(
    website.site_title ||
      renderedContent.value?.seo?.title ||
      renderedContent.value?.hero?.hero_headline ||
      website.domain ||
      website.quotastic_domain ||
      selectedTemplate.value?.name ||
      'Published Site'
  )
)

watch(
  pageTitle,
  (title) => {
    if (title) {
      document.title = title
    }
  },
  { immediate: true }
)

const componentKey = computed(() =>
  [
    website.id || routeIdentifier.value || 'site',
    selectedTemplate.value?.id || website.template_id || 'template'
  ].join(':')
)

// Stable empty shape keeps the template bindings predictable while remote data loads.
function createEmptyWebsite() {
  return {
    id: null,
    company_id: null,
    domain: '',
    quotastic_domain: '',
    site_title: '',
    template_id: '',
    content: null,
  }
}

function resetWebsite() {
  Object.assign(website, createEmptyWebsite())
}

function hydrateWebsite(record) {
  resetWebsite()
  Object.assign(website, {
    ...record,
    content: parseContent(record.content),
  })
}

// Backend content can arrive as JSON text or an object depending on the caller.
function parseContent(rawContent) {
  if (!rawContent) {
    return {}
  }

  if (typeof rawContent === 'string') {
    try {
      return JSON.parse(rawContent)
    }
    catch (error) {
      console.error('Unable to parse website content:', error)
      return {}
    }
  }

  if (typeof rawContent === 'object') {
    return rawContent
  }

  return {}
}

function cloneContent(value) {
  if (!value) {
    return {}
  }

  if (typeof structuredClone === 'function') {
    return structuredClone(value)
  }

  return JSON.parse(JSON.stringify(value))
}

// Content merge helpers preserve template field descriptors while replacing edited values.
function isPlainObject(value) {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value)
  )
}

function isFieldDescriptor(value) {
  return isPlainObject(value) && 'value' in value
}

// Recursively merge saved content into template content without losing editor metadata.
function mergeContent(baseContent, savedContent) {
  if (isFieldDescriptor(baseContent)) {
    if (isFieldDescriptor(savedContent)) {
      return {
        ...baseContent,
        ...savedContent,
        value: mergeContent(baseContent.value, savedContent.value)
      }
    }

    return savedContent == null
      ? baseContent
      : {
          ...baseContent,
          value: savedContent
        }
  }

  if (Array.isArray(baseContent) || Array.isArray(savedContent)) {
    return Array.isArray(savedContent) ? savedContent : baseContent
  }

  if (!isPlainObject(baseContent) || !isPlainObject(savedContent)) {
    return savedContent ?? baseContent
  }

  const merged = { ...baseContent }

  for (const key of Object.keys(savedContent)) {
    merged[key] = key in baseContent
      ? mergeContent(baseContent[key], savedContent[key])
      : savedContent[key]
  }

  return merged
}

// A preview URL can identify a website by several fields, so try each unique query shape.
function uniqueQueries(identifier) {
  const encoded = encodeURIComponent(identifier)
  const queries = [
    `quotastic_domain=${encoded}`,
    `domain=${encoded}`,
    `company_id=${encoded}`,
  ]

  if (/^\d+$/.test(identifier)) {
    queries.splice(2, 0, `id=${encoded}`)
  }

  return [...new Set(queries)]
}

// Fetch one possible website match. Live builder edits are supplied over postMessage.
async function fetchWebsite(query) {
  const response = await fetch(`${API_BASE_URL}/websites_draft?${query}`)
  const data = await response.json().catch(() => ({}))

  if (!response.ok) {
    throw new Error(data.error || 'Failed to load website')
  }

  return data.websites?.[0] || null
}

// Main loader for the route. It resets preview state, tries each identifier lookup,
// and ignores stale results when a newer load starts before this one finishes.
async function retrieveWebsite(identifier = routeIdentifier.value) {
  const currentRun = ++loadRun
  const siteIdentifier = String(identifier || '').trim()

  resetWebsite()
  loading.value = true
  loadError.value = ''
  hasLoaded.value = false

  if (!siteIdentifier) {
    loading.value = false
    hasLoaded.value = true
    loadError.value = 'No site was specified.'
    return null
  }

  try {
    for (const query of uniqueQueries(siteIdentifier)) {
      const record = await fetchWebsite(query)

      if (currentRun !== loadRun) {
        return null
      }

      if (record) {
        hydrateWebsite(record)
        return record
      }
    }

    loadError.value = 'Site not found.'
    return null
  }
  catch (error) {
    if (currentRun !== loadRun) {
      return null
    }

    console.error('retrieveWebsite error:', error)
    loadError.value = error.message || 'Failed to load website.'
    return null
  }
  finally {
    if (currentRun === loadRun) {
      loading.value = false
      hasLoaded.value = true
    }
  }
}

// Initial data available to the runtime component generated from the template.
function baseData(siteContent) {
  return {
    content: cloneContent(siteContent || {}),
    scrolled: false,
    menuOpen: false,
    formSent: false,
    formSubmitting: false,
    formError: '',
    formConfirmation: null,
    form: {
      name: '',
      email: '',
      service: '',
      message: ''
    }
  }
}

// Templates may store contact email as a plain string or an editor field descriptor.
function contactEmailFromContent(content) {
  const email = content?.contact?.email || content?.contact_email || ''

  if (email && typeof email === 'object') {
    return email.value || ''
  }

  return email || ''
}

// Normalize editor field descriptors and primitive values into displayable text.
function text(value) {
  if (value && typeof value === 'object' && 'value' in value) {
    return text(value.value)
  }

  return String(value || '').trim()
}

// Compile the selected template into a Vue component once the website and template are ready.
const compiledComponent = computed(() => {
  const site = selectedTemplate.value

  if (!hasLoaded.value || loading.value || loadError.value || !site) {
    return null
  }

  return defineComponent({
    template: site.template,

    props: {
      siteContent: {
        type: Object,
        default: () => ({})
      }
    },

    data() {
      return baseData(this.siteContent)
    },

    watch: {
      siteContent: {
        deep: true,
        handler(nextContent) {
          this.content = cloneContent(nextContent || {})
        }
      }
    },

    methods: {
      // Sends contact form submissions through the shared Quotastic API.
      async submitForm(e) {
        e?.preventDefault?.()

        if (this.formSubmitting) {
          return null
        }

        const routeId = routeIdentifier.value
        const payload = {
          site_id: website.id || routeId || null,
          ...(website.company_id ? { company_id: website.company_id } : {}),
          name: this.form.name,
          email: this.form.email,
          service: this.form.service,
          message: this.form.message,
          contractor_email: contactEmailFromContent(this.content),
          page_url: window.location.href
        }

        this.formSubmitting = true
        this.formSent = false
        this.formError = ''
        this.formConfirmation = null

        try {
          const response = await fetch(`${API_BASE_URL}/form-submission`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          })
          const confirmation = await response.json().catch(() => null)

          if (!response.ok) {
            throw new Error(
              confirmation?.message ||
              confirmation?.error ||
              'Unable to submit form.'
            )
          }

          this.formSent = true
          this.formConfirmation = confirmation
          this.form = {
            name: '',
            email: '',
            service: '',
            message: ''
          }

          setTimeout(() => {
            this.formSent = false
          }, 3000)

          return confirmation
        }
        catch (error) {
          this.formError = error.message || 'Unable to submit form.'
          console.error('Form submission failed:', error)

          return null
        }
        finally {
          this.formSubmitting = false
        }
      }
    },

    // Template code can react to scroll state without each template wiring its own listener.
    mounted() {
      this._scrollTarget =
        document.querySelector('.content') || window

      this._onScroll = () => {
        const scrollTop =
          this._scrollTarget === window
            ? window.scrollY
            : this._scrollTarget.scrollTop

        this.scrolled = scrollTop > 0
      }

      this._onScroll()
      this._scrollTarget.addEventListener('scroll', this._onScroll)
    },

    unmounted() {
      this._scrollTarget?.removeEventListener(
        'scroll',
        this._onScroll
      )
    }
  })
})

// Reload the preview whenever the route id/domain/company identifier changes.
watch(
  routeIdentifier,
  (identifier) => {
    retrieveWebsite(identifier)
  },
  { immediate: true }
)

const resolvedCss = computed(() => {
  const css = selectedTemplate.value?.css
  if (!css) return ''

  return css.replace(/\{\{content\.([^}]+)\}\}/g, (_match, path) => {
    const parts = path.trim().split('.')
    let val = renderedContent.value
    for (const part of parts) {
      val = val?.[part]
      if (val === undefined || val === null) return ''
    }
    return typeof val === 'object' ? (val.value ?? '') : String(val)
  })
})

const cssKey = ref(0)
watch(resolvedCss, () => { cssKey.value++ })
</script>



<template>
  <div class="site-view">
    <div
      v-if="loading"
      class="site-status"
    >
      Loading site...
    </div>

    <div
      v-else-if="loadError"
      class="site-status site-status-error"
    >
      {{ loadError }}
    </div>

    <template v-else-if="selectedTemplate && compiledComponent">
      <component :is="'style'" :key="cssKey">
        {{ resolvedCss }}
      </component>

      <component
        :is="compiledComponent"
        :key="componentKey"
        :site-content="renderedContent"
      />
    </template>

    <div
      v-else
      class="site-status site-status-error"
    >
      Site template not found.
    </div>
  </div>
</template>



<style scoped>
.site-view {
  min-height: 100vh;
}

.site-status {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 32px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: 600 1rem/1.4 system-ui, sans-serif;
}

.site-status-error {
  color: #9f2a1d;
}
</style>
